"use client"

import { useState } from "react"
import CompactToolCta from "@/app/components/atomic/atoms/CompactToolCta"
import { useCopyFeedback } from "@/app/hooks/useCopyFeedback"
import {
    allProfilesCell,
    formatEur,
    formatNumber,
    formatRate,
    formatRateValue,
    listFr,
    monthlyToRate,
    parseNumber,
    PERCENTILES,
    plainNumber,
    positionFor,
    rateToMonthly,
    readForm,
    TOOL_META,
    type CostMode,
    type Smoker,
    type WorkStatus,
} from "@/app/utils/loan-insurance-percentile"
import "@/app/styles/tools/outil-compact.scss"
import "@/app/styles/tools/percentile-assurance-pret-compact.scss"

const CTA_URL =
    "https://reassurez-moi.fr/simulation-assurance-pret-immobilier?utm_source=guide&utm_medium=outil&utm_campaign=percentile-taea&utm_content=compact"

const NBSP = " "

const STATUS_OPTIONS: { value: WorkStatus; label: string }[] = [
    { value: "CAD", label: "Cadre, libéral, chef d'entreprise" },
    { value: "NCA", label: "Non cadre, fonctionnaire, retraité" },
    { value: "MAN", label: "Ouvrier, artisan, indépendant" },
]

const SMOKER_OPTIONS: { value: Smoker; label: string }[] = [
    { value: "NF", label: "Non" },
    { value: "F", label: "Oui" },
]

const quantile = (q: number[], percentile: number): number => q[PERCENTILES.indexOf(percentile)]

type Field = "age" | "capital" | "years"

const PercentileAssurancePretCompact = () => {
    const [values, setValues] = useState<Record<Field, string>>({ age: "44", capital: "250 000", years: "18" })
    const [smoker, setSmoker] = useState<Smoker>("NF")
    const [status, setStatus] = useState<WorkStatus>("NCA")
    const [mode, setMode] = useState<CostMode>("taux")
    const [cost, setCost] = useState("0,36")
    const { label: copyLabel, copy } = useCopyFeedback("Copier la phrase")

    const setField = (field: Field, value: string) => setValues((previous) => ({ ...previous, [field]: value }))

    const form = readForm({
        ...values,
        smoker,
        status,
        mode,
        rate: mode === "taux" ? cost : "",
        monthly: mode === "euro" ? cost : "",
    })
    const result = positionFor(form)
    const q = (result?.cell ?? allProfilesCell()).q
    const bestFifth = quantile(q, 20)
    const median = quantile(q, 50)
    const worstFifth = quantile(q, 80)

    const reformat = (field: Field) => {
        const parsed = parseNumber(values[field])

        if (parsed !== null) {
            setField(field, formatNumber(parsed))
        }
    }

    const reformatCost = () => {
        const parsed = parseNumber(cost)

        if (parsed !== null) {
            setCost(mode === "euro" ? formatNumber(Math.round(parsed)) : formatRateValue(parsed))
        }
    }

    const changeMode = (nextMode: CostMode) => {
        const parsed = parseNumber(cost)

        if (parsed !== null && nextMode !== mode) {
            // Without a borrowed capital nothing converts the figure, and keeping it would silently change its meaning
            if (form.capital === null) {
                setCost("")
            } else if (nextMode === "euro") {
                setCost(formatNumber(Math.round(rateToMonthly(parsed, form.capital))))
            } else {
                setCost(formatRateValue(monthlyToRate(parsed, form.capital)))
            }
        }

        setMode(nextMode)
    }

    const paysMore = result !== null && result.shown >= 50
    const shareSentence =
        result === null
            ? ""
            : paysMore
              ? `« Je paie mon assurance de prêt plus cher que ${result.shown} % des emprunteurs de mon profil` +
                (result.overcost >= 1
                    ? `. Cela me coûtera ${plainNumber(Math.round(result.overcost))} € d'ici la fin de mon prêt. »`
                    : ". »")
              : `« Je paie mon assurance de prêt moins cher que ${100 - result.shown} % des emprunteurs de mon profil. »`

    const onCopy = () => {
        if (!shareSentence) return

        const pageUrl = `${window.location.origin}${window.location.pathname}?utm_source=partage&utm_medium=phrase&utm_campaign=percentile-taea`

        copy(`${shareSentence} (Réassurez-Moi) ${pageUrl}`)
    }

    const savingSummary = () => {
        if (result === null || form.rate === null || form.capital === null) {
            return { value: "…", text: null, cta: "Obtenir mes vrais tarifs" }
        }

        if (result.overcost >= 1) {
            const monthly = rateToMonthly(form.rate - result.median, form.capital)

            return {
                value: formatEur(result.overcost),
                text: (
                    <>
                        à économiser d&apos;ici la fin du prêt au tarif médian de votre profil, soit{" "}
                        {formatEur(monthly)} par mois
                        <span className="rmc-pct-more">
                            Jusqu&apos;à {formatEur(result.overcostBestFifth)} au niveau des 20 % les mieux placés
                        </span>
                    </>
                ),
                cta: `Aller chercher ces ${formatEur(result.overcost)}`,
            }
        }

        if (result.overcostBestFifth >= 1) {
            return {
                value: formatEur(result.overcostBestFifth),
                text: `à économiser d'ici la fin du prêt au niveau des 20 % les mieux placés de votre profil (${formatRate(bestFifth)})`,
                cta: `Aller chercher ces ${formatEur(result.overcostBestFifth)}`,
            }
        }

        return {
            value: "Top 20 %",
            text: "Vous faites partie des 20 % les mieux placés de votre profil. Reste à vérifier que vos garanties suivent.",
            cta: "Vérifier mes garanties",
        }
    }

    const saving = savingSummary()

    const field = (name: Field, label: string, unit: string, className: string) => (
        <label className={`rmc-f ${className}`}>
            <span className="rmc-l">{label}</span>
            <span className="rmc-in">
                <input
                    data-testid={`pctc-${name}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={values[name]}
                    onChange={(event) => setField(name, event.target.value)}
                    onBlur={() => reformat(name)}
                />
                <span className="rmc-u">{unit}</span>
            </span>
        </label>
    )

    return (
        <div className="rmc rmc-pct" data-testid="percentile-assurance-pret-compact">
            <div className="rmc-grid rmc-pct-fields">
                {field("age", "Votre âge", "ans", "rmc-pct-age")}
                <div className="rmc-f rmc-pct-smk">
                    <span className="rmc-l">Fumeur, vape comprise</span>
                    <span className="rmc-seg" role="group" aria-label="Fumeur, vape comprise">
                        {SMOKER_OPTIONS.map((option) => (
                            <button
                                key={option.value}
                                type="button"
                                aria-pressed={smoker === option.value}
                                data-testid={`pctc-smoker-${option.value}`}
                                onClick={() => setSmoker(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </span>
                </div>
                <label className="rmc-f rmc-pct-st">
                    <span className="rmc-l">Statut professionnel</span>
                    <span className="rmc-in">
                        <select
                            className="rmc-sel rmc-sel-full"
                            data-testid="pctc-status"
                            value={status}
                            onChange={(event) => setStatus(event.target.value as WorkStatus)}
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </span>
                </label>
                {field("capital", "Capital emprunté", "€", "rmc-pct-cap")}
                {field("years", "Durée restante", "ans", "rmc-pct-yr")}
                <label className="rmc-f rmc-pct-cost">
                    <span className="rmc-l">Ce que vous payez</span>
                    <span className="rmc-in">
                        <input
                            data-testid="pctc-cost"
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={cost}
                            onChange={(event) => setCost(event.target.value)}
                            onBlur={reformatCost}
                        />
                        <select
                            className="rmc-sel"
                            data-testid="pctc-mode"
                            aria-label="Unité"
                            value={mode}
                            onChange={(event) => changeMode(event.target.value as CostMode)}
                        >
                            <option value="taux">% par an</option>
                            <option value="euro">€ par mois</option>
                        </select>
                    </span>
                </label>
            </div>

            <div className="rmc-pct-res">
                <div className="rmc-pct-stats" aria-live="polite">
                    <div>
                        <p className="rmc-p rmc-pct-v">
                            <span className="rmc-mark" data-testid="pctc-percentile">
                                {result === null ? "…" : `${paysMore ? result.shown : 100 - result.shown}${NBSP}%`}
                            </span>
                        </p>
                        <p className="rmc-p rmc-pct-claim" data-testid="pctc-claim">
                            {result === null
                                ? form.euroOutOfRange
                                    ? "La cotisation saisie donne un taux hors de la plage mesurable (0 à 3 % par an)."
                                    : `Il manque ${listFr(form.missing)} pour vous situer.`
                                : paysMore
                                  ? "des emprunteurs de votre profil paient moins cher que vous"
                                  : "des emprunteurs de votre profil paient plus cher que vous"}
                        </p>
                    </div>
                    <div className="rmc-pct-stat2">
                        <p className="rmc-p rmc-pct-v rmc-pct-v2" data-testid="pctc-saving">
                            {saving.value}
                        </p>
                        {saving.text && <p className="rmc-p rmc-pct-claim">{saving.text}</p>}
                    </div>
                </div>
                <div
                    className="rmc-pct-track"
                    role="img"
                    aria-label={
                        result === null || form.rate === null
                            ? `Médiane ${formatRate(median)}, 20 % les mieux placés sous ${formatRate(bestFifth)}.`
                            : `Votre taux, ${formatRate(form.rate)}, se place au ${result.shown}e percentile du profil. ` +
                              `Médiane ${formatRate(median)}, 20 % les mieux placés sous ${formatRate(bestFifth)}.`
                    }
                >
                    <div className="rmc-pct-bar">
                        <span className="rmc-pct-z1" />
                        <span className="rmc-pct-z2" />
                        <span className="rmc-pct-z3" />
                    </div>
                    <span className="rmc-pct-end" style={{ left: 0 }}>
                        moins cher
                    </span>
                    <span className="rmc-pct-end" style={{ right: 0 }}>
                        plus cher
                    </span>
                    <span className="rmc-pct-lab" style={{ left: "20%" }}>
                        {formatRate(bestFifth)}
                    </span>
                    <span className="rmc-pct-lab" style={{ left: "50%" }}>
                        Médiane <span className="rmc-b">{formatRate(median)}</span>
                    </span>
                    <span className="rmc-pct-lab" style={{ left: "80%" }}>
                        {formatRate(worstFifth)}
                    </span>
                    {result !== null && form.rate !== null && (
                        <span className="rmc-pct-me" style={{ left: `${result.shown}%` }}>
                            <span className="rmc-pct-pill" style={{ transform: `translateX(-${result.shown}%)` }}>
                                Vous · {formatRate(form.rate)}
                            </span>
                        </span>
                    )}
                </div>
                <div className="rmc-go">
                    <CompactToolCta href={CTA_URL} label={saving.cta} testId="pctc-cta" />
                    <span className="rmc-micro">Gratuit, et possible à tout moment depuis la loi Lemoine.</span>
                    <button type="button" className="rmc-link" data-testid="pctc-copy" onClick={onCopy}>
                        {copyLabel}
                    </button>
                </div>
            </div>

            <p className="rmc-p rmc-note">
                Repère établi sur les changements d&apos;assurance de prêt menés avec Réassurez-Moi depuis{" "}
                {TOOL_META.fenetre.mois} mois, à profil comparable. Seul un devis établit votre tarif réel.
            </p>
        </div>
    )
}

export default PercentileAssurancePretCompact
